import { ActionIcon, Tooltip } from '@mantine/core';
import { useClickOutside, useDisclosure } from '@mantine/hooks';
import { IconMoodSmile, IconSend, IconUsers } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EmojiPicker } from 'src/Components/EmojiPicker';
import { MentionTextarea } from 'src/Components/MentionTextarea';
import UserAvatar from 'src/Components/NavBar/UserAvatar';
import useFetchAllMembers from 'src/Hooks/Queries/useFetchAllMembers';
import useFetchUser from 'src/Hooks/Queries/useFetchUser';
import useIsMobile from 'src/Hooks/Shared/useIsMobile';

import styles from './CreateComment.module.scss';

export type CreateCommentProps = {
  /** Remplace « Écrire un commentaire » — le mur parle de « répondre ». */
  placeholder?: string;
  onSubmit: (message: string) => void;
  isLoading?: boolean;
};

const CreateComment = ({ onSubmit, isLoading, placeholder }: CreateCommentProps) => {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [isEmojiPickerOpen, { close: closeEmojiPicker, toggle: toggleEmojiPicker }] =
    useDisclosure(false);
  const clickOutsideRef = useClickOutside(closeEmojiPicker);

  const { data: membersData } = useFetchAllMembers();
  const { data: me } = useFetchUser();

  const otherMembers = (membersData?.members ?? []).filter(member => {
    if (!member.user || member.user.id === me?.id) return false;
    const name = `${member.user.firstName ?? ''} ${member.user.lastName ?? ''}`.trim();
    return name.length > 0;
  });

  const mentionAll = () => {
    if (otherMembers.length === 0) return;

    const mentions = otherMembers
      .map(member => {
        const name = `${member.user.firstName ?? ''} ${member.user.lastName ?? ''}`.trim();
        return `@[${name}](${member.user.id})`;
      })
      .join(' ');

    setText(prev => {
      const needsLeadingSpace = prev.length > 0 && !prev.endsWith(' ');
      return `${prev}${needsLeadingSpace ? ' ' : ''}${mentions} `;
    });
  };

  const createComment = () => {
    const trim = text.trim();

    if (!trim) return;

    onSubmit(trim);
    setText('');
  };

  const onEmoji = (emoji: string) => {
    setText(t => t + emoji);
  };
  const showEmojiPicker = !useIsMobile();

  return (
    <div className={styles.container} ref={clickOutsideRef}>
      <UserAvatar size={36} />

      <MentionTextarea
        value={text}
        onChange={setText}
        placeholder={placeholder ?? t('community.comments.placeholder', 'Écrire un commentaire')}
      />

      {otherMembers.length > 0 ? (
        <Tooltip label={t('community.comments.mentionAll', 'Mentionner tout le monde')}>
          <ActionIcon
            onClick={mentionAll}
            variant="outline"
            className={styles.icon}
            aria-label={t('community.comments.mentionAll', 'Mentionner tout le monde')}
          >
            <IconUsers size={20} />
          </ActionIcon>
        </Tooltip>
      ) : null}

      {showEmojiPicker ? (
        <ActionIcon onClick={toggleEmojiPicker} variant="outline" className={styles.icon}>
          <IconMoodSmile size={20} />
        </ActionIcon>
      ) : null}

      <ActionIcon
        variant="outline"
        className={styles.icon}
        onClick={createComment}
        loading={isLoading}
      >
        <IconSend size={20} />
      </ActionIcon>

      <div className={styles.emojiPicker}>
        <EmojiPicker visible={isEmojiPickerOpen} onEmoji={onEmoji} />
      </div>
    </div>
  );
};

export default CreateComment;
